import React, {useState, useEffect, useCallback, useMemo} from 'react';
import './chat.css';
import LottieButtonChatCTA from '../components/LottieButtonChatCTA';
import ChatWindow from './ChatWindow';
import {ChatIcon, StyledContainer, TooltipContent} from './styles';
import useAmbit from './useAmbit';
import Fuse from 'fuse.js';
import {Prompt} from './types';
import {config} from '../environment';
import debounce from 'lodash/debounce';
import {useSendChatAnalytic} from '../hooks/useSendChatAnalytic';
import bodyScrollLock from '../utils/bodyScrollLock';

const DEFAULT_BOTTOM_OFFSET = 16;

const EveeChatCta: React.FC = () => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [isActive, setIsActive] = useState(false);
  const {sendMessage, loading, messages} = useAmbit({
    open: isOpen,
  });
  const [promptSuggestions, setPromptSuggestions] = useState<string[]>([]);
  const [bottomOffset, setBottomOffset] = useState<number>(
    DEFAULT_BOTTOM_OFFSET,
  );
  const {handleChatCTAClick} = useSendChatAnalytic();

  const toggleTooltip = useCallback(() => {
    setIsActive((prevIsActive) => !prevIsActive);
  }, []);

  const openModal = useCallback(() => {
    setIsOpen(true);
    handleChatCTAClick();
    bodyScrollLock.enable();
  }, [handleChatCTAClick]);

  const closeModal = useCallback(() => {
    setIsOpen(false);
    bodyScrollLock.disable();
  }, []);

  const getPromptSuggestions = useCallback((data: Prompt[]) => {
    const MINIMUM_PROMPT_SUGGESTIONS = 4;
    const fuseOptions = {
      keys: ['prompt', 'keywords'],
      findAllMatches: true,
      threshold: 0.5,
    };
    const promptSearchInstance = new Fuse(data, fuseOptions);
    const siteTitle = document.title;
    const metaDescription = document.querySelector("meta[name='description']");
    const siteDescription = metaDescription?.getAttribute('content') || '';
    const searchQuery = `${siteTitle} ${siteDescription}`;

    let promptSuggestions = promptSearchInstance
      .search(searchQuery)
      .map((result) => result.item.prompt);

    if (promptSuggestions.length < MINIMUM_PROMPT_SUGGESTIONS) {
      promptSuggestions = promptSuggestions.concat(
        data
          .filter((item) => !promptSuggestions.includes(item.prompt))
          .map((item) => item.prompt)
          .slice(0, MINIMUM_PROMPT_SUGGESTIONS - promptSuggestions.length),
      );
    }
    return promptSuggestions;
  }, []);

  const fetchPrompts = useCallback(async () => {
    try {
      const promptsResponse = await fetch(`${config.API_EV_CHAT_PROMPTS}`, {
        headers: {'Content-Type': 'application/json'},
      });
      const {data}: {data: Prompt[] | null} = await promptsResponse.json();

      if (data && data.length > 0) {
        const suggestions = getPromptSuggestions(data);
        setPromptSuggestions(suggestions);
      }
    } catch (error) {
      console.error('Error:', error);
    }
  }, [getPromptSuggestions]);

  const debouncedSetBottomOffset = useMemo(
    () =>
      debounce(() => {
        const bottomSheetContainer = document.querySelector(
          'div[class^="StyledNextBestActionPanelWrapper"]',
        );

        if (bottomSheetContainer) {
          const div = bottomSheetContainer.querySelector(
            'div[class^="StyledWrapper"]',
          ) as HTMLDivElement;

          const offset = (div?.offsetHeight ?? 0) + 24;
          setBottomOffset(offset);
        } else {
          setBottomOffset(DEFAULT_BOTTOM_OFFSET);
        }
      }, 100),
    [],
  );

  useEffect(() => {
    const chatAppElement = document.getElementById('chat-bubble');

    const applyMarginToParents = (element: HTMLElement | null) => {
      let parent = element?.parentElement;
      while (parent) {
        if (
          parent.className &&
          parent.className
            .split(' ')
            .some((className) => className.includes('StyledChildWrapper'))
        ) {
          parent.style.margin = '0';
        }
        parent = parent.parentElement;
      }
    };

    if (chatAppElement) {
      applyMarginToParents(chatAppElement);
    } else {
      console.log('Element with ID "chat-bubble" not found.');
    }
  }, []);

  useEffect(() => {
    window.addEventListener('scroll', debouncedSetBottomOffset);

    return () => {
      window.removeEventListener('scroll', debouncedSetBottomOffset);
    };
  }, [debouncedSetBottomOffset]);

  useEffect(() => {
    fetchPrompts();
  }, [fetchPrompts]);

  return (
    <div id="chat-bubble">
      <StyledContainer bottomOffset={bottomOffset}>
        <TooltipContent
          style={{
            visibility: isActive ? 'visible' : 'hidden',
            opacity: isActive ? 1 : 0,
          }}
        >
          <span>Ask a question</span>
        </TooltipContent>
        <ChatIcon>
          <LottieButtonChatCTA
            onClick={openModal}
            onMouseEnter={toggleTooltip}
            onMouseLeave={toggleTooltip}
          />
        </ChatIcon>
      </StyledContainer>
      <ChatWindow
        active={isOpen}
        onClose={closeModal}
        messages={messages}
        handleSendMessage={sendMessage}
        loading={loading}
        promptSuggestions={promptSuggestions}
      />
    </div>
  );
};

export default EveeChatCta;
